import { useEffect, useRef } from "react";

/**
 * Animated red blueprint of a luxury mansion with parallax scroll + pulsing
 * risk markers. Drawn fully in SVG, scales responsively.
 */
export default function LuxuryResidentialVisual() {
  const wrapRef = useRef(null);
  const houseRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(hover: none), (max-width: 768px)").matches) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!wrapRef.current || !houseRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        const vh = window.innerHeight;
        const progress = Math.max(-1, Math.min(1, (vh / 2 - rect.top) / vh));
        houseRef.current.style.transform = `translate3d(0, ${progress * -22}px, 0)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200 bg-[#fafafa] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.12)]"
    >
      <div className="absolute inset-0 btr-blueprint-overlay opacity-80" />
      <div className="absolute top-3 left-3 text-[10px] font-display tracking-[0.3em] uppercase text-[#a2020b]">
        Luxury Residential · Pre-Loss Schematic
      </div>
      <div className="absolute top-3 right-3 text-[10px] font-display tracking-[0.2em] uppercase text-[#a2020b] flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#a2020b] btr-red-glow" />
        SCAN ACTIVE
      </div>

      <svg
        ref={houseRef}
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full transition-transform will-change-transform"
      >
        <g fill="none" stroke="#a2020b" strokeWidth="1.4">
          {/* Main house body */}
          <path d="M 120 480 L 120 260 L 200 260 L 200 180 L 420 180 L 420 260 L 500 260 L 500 480 Z" />
          {/* Roof */}
          <path d="M 200 180 L 310 90 L 420 180" strokeDasharray="4 4" opacity="0.7" />
          <path d="M 310 90 L 310 130" opacity="0.4" />
          {/* East wing */}
          <path d="M 500 320 L 680 320 L 680 480 L 500 480" />
          <path d="M 500 320 L 590 250 L 680 320" strokeDasharray="4 4" opacity="0.7" />
          {/* Pool */}
          <rect x="540" y="500" width="130" height="60" rx="4" opacity="0.6" />
          <path d="M 555 530 L 660 530" strokeDasharray="2 3" opacity="0.5" />
          {/* West garage wing */}
          <path d="M 120 340 L 40 340 L 40 480 L 120 480" opacity="0.85" />
          <path d="M 50 400 L 110 400" opacity="0.4" />
          <path d="M 50 430 L 110 430" opacity="0.4" />
          {/* Driveway */}
          <path d="M 80 480 L 80 570 L 720 570" opacity="0.35" strokeDasharray="5 6" />
          {/* Windows main */}
          <path d="M 150 300 L 180 300" strokeWidth="2.4" />
          <path d="M 220 300 L 260 300" strokeWidth="2.4" />
          <path d="M 360 300 L 400 300" strokeWidth="2.4" />
          <path d="M 440 300 L 480 300" strokeWidth="2.4" />
          <path d="M 150 380 L 180 380" strokeWidth="2.4" />
          <path d="M 220 380 L 260 380" strokeWidth="2.4" />
          <path d="M 360 380 L 400 380" strokeWidth="2.4" />
          <path d="M 440 380 L 480 380" strokeWidth="2.4" />
          {/* Front door */}
          <path d="M 290 480 L 290 430 L 340 430 L 340 480" strokeWidth="1.8" />
          {/* Interior partitions */}
          <path d="M 120 380 L 500 380" opacity="0.35" />
          <path d="M 310 260 L 310 480" opacity="0.3" />
          {/* Heavy outline */}
          <path d="M 40 480 L 680 480" strokeWidth="2.2" />
        </g>

        {/* Dimension ticks */}
        <g stroke="#a2020b" strokeWidth="0.9" opacity="0.45">
          <path d="M 40 510 L 680 510" />
          <path d="M 40 506 L 40 514" />
          <path d="M 680 506 L 680 514" />
          <path d="M 360 506 L 360 514" />
        </g>
        <text x="340" y="528" fontSize="10" fill="#a2020b" fontFamily="Outfit, sans-serif" letterSpacing="3" opacity="0.75">
          64ft
        </text>

        {/* Compass */}
        <g transform="translate(730, 100)" opacity="0.75">
          <circle r="22" fill="none" stroke="#a2020b" strokeWidth="1" />
          <path d="M 0 -18 L 0 18 M -18 0 L 18 0" stroke="#a2020b" strokeWidth="0.7" />
          <path d="M 0 -18 L -4 -8 L 0 -10 L 4 -8 Z" fill="#a2020b" />
          <text x="0" y="-26" fill="#a2020b" fontSize="9" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700">
            N
          </text>
        </g>
      </svg>

      {/* Risk markers absolutely positioned (percent coords) */}
      {[
        { top: "16%", left: "39%", label: "Roof Ridge" },
        { top: "44%", left: "20%", label: "Windows — West" },
        { top: "44%", left: "56%", label: "Windows — East" },
        { top: "74%", left: "38%", label: "Front Entry" },
        { top: "82%", left: "76%", label: "Pool / Water Intrusion" },
        { top: "58%", left: "82%", label: "East Wing Roof" },
        { top: "72%", left: "9%", label: "Garage Envelope" },
      ].map((m, i) => (
        <div
          key={i}
          className="absolute flex items-center gap-2"
          style={{ top: m.top, left: m.left, animation: `btr-float 4.5s ease-in-out ${i * 0.3}s infinite` }}
        >
          <span className="relative">
            <span className="absolute inset-0 rounded-full bg-[#a2020b]/30 animate-ping" />
            <span className="relative block w-2.5 h-2.5 rounded-full bg-[#a2020b] btr-red-glow" />
          </span>
          <span className="hidden sm:inline text-[10px] font-display font-semibold uppercase tracking-[0.15em] text-[#a2020b] bg-white/70 backdrop-blur-sm px-2 py-0.5 rounded-sm">
            {m.label}
          </span>
        </div>
      ))}

      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-[2px] bg-[#a2020b] opacity-70"
        style={{ animation: "btr-scan 5s ease-in-out infinite", boxShadow: "0 0 14px #a2020b" }}
      />
    </div>
  );
}
